import { motion } from "framer-motion";
import { AlertTriangle, CheckCircle2, ArrowRight, Search, Building2, Smartphone } from "lucide-react";
import { Reveal, Kicker, SectionHeading } from "@/components/site/primitives";
import { SCENARIOS, PALLET_FLOW, PALLET_LEDGER, IMAGES } from "@/data";

export const MasterPlanner = () => (
  <section className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
    <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr]">
      <Reveal>
        <Kicker num="04">Master Planner</Kicker>
        <SectionHeading className="mt-6">What if?</SectionHeading>
        <p className="mt-6 text-lg leading-relaxed text-slate-500">
          Plan tomorrow before it becomes a problem. New customer? Black Friday? Four
          containers arriving at once? Model the scenario and see the impact on capacity
          before it hits the floor.
        </p>
      </Reveal>

      <div className="flex flex-col gap-4">
        {SCENARIOS.map((s, i) => (
          <Reveal key={s.id} delay={i * 0.08}>
            <motion.div
              whileHover={{ x: 8 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              data-testid={`scenario-${s.id}`}
              className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-6 transition-colors hover:border-volt/50"
            >
              <div className="flex items-center gap-6">
                <span className="font-display text-4xl font-black text-stroke">{s.id}</span>
                <div>
                  <div className="font-mono-p text-[10px] uppercase tracking-widest text-slate-400">Scenario</div>
                  <div className="font-display text-xl font-bold text-slate-900">{s.title}</div>
                  <div className="mt-1 font-mono-p text-sm text-volt">{s.metric}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono-p text-[10px] uppercase tracking-widest text-slate-400">{s.label}</div>
                <div className={`flex items-center justify-end gap-2 font-display text-2xl font-bold ${s.tone === "warn" ? "text-destructive" : "text-volt"}`}>
                  {s.tone === "warn" ? <AlertTriangle className="h-5 w-5" /> : <CheckCircle2 className="h-5 w-5" />}
                  {s.value}
                </div>
              </div>
            </motion.div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const PalletControl = () => (
  <section className="relative overflow-hidden border-y border-slate-200 bg-[#F4F6FB]">
    <div className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
      <div className="grid gap-14 lg:grid-cols-2">
        <Reveal>
          <Kicker num="05">Pallet Control</Kicker>
          <SectionHeading className="mt-6">Where are your pallets?</SectionHeading>
          <p className="mt-6 text-lg leading-relaxed text-slate-500">
            Stop losing pallets. Track every movement across CHEP, LOSCAM and more —
            then reconcile against the provider statement automatically.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-y-3 font-mono-p text-xs uppercase tracking-wider">
            {PALLET_FLOW.map((step, i) => (
              <div key={step} className="flex items-center">
                <span className="rounded-md border border-slate-200 bg-white px-3 py-2 text-slate-600">{step}</span>
                {i < PALLET_FLOW.length - 1 && <ArrowRight className="mx-1.5 h-4 w-4 text-volt" />}
              </div>
            ))}
          </div>

          <div className="mt-10 flex items-center gap-6 font-mono-p text-sm text-slate-400">
            <span className="text-slate-700">CHEP</span>
            <span className="h-4 w-px bg-slate-300" />
            <span className="text-slate-700">LOSCAM</span>
            <span className="h-4 w-px bg-slate-300" />
            <span className="text-slate-700">Other</span>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-[0_30px_80px_-50px_rgba(15,23,42,0.4)]">
            <div className="mb-5 flex items-center justify-between border-b border-slate-200 pb-4">
              <span className="font-display text-lg font-bold text-slate-900">CHEP · Pallet Position</span>
              <span className="font-mono-p text-[10px] uppercase tracking-widest text-slate-400">This month</span>
            </div>
            <div className="space-y-3">
              {PALLET_LEDGER.map((row) => (
                <div key={row.label} className="flex items-center justify-between font-mono-p text-sm">
                  <span className="text-slate-500">{row.label}</span>
                  <span className={row.d === "down" ? "text-destructive" : row.d === "up" ? "text-volt" : "text-slate-900"}>{row.value}</span>
                </div>
              ))}
              <div className="my-2 h-px bg-slate-200" />
              <div className="flex items-center justify-between font-mono-p text-sm">
                <span className="text-slate-700">Expected position</span>
                <span className="font-bold text-slate-900">325</span>
              </div>
              <div className="flex items-center justify-between font-mono-p text-sm">
                <span className="text-slate-700">CHEP statement</span>
                <span className="font-bold text-slate-900">270</span>
              </div>
            </div>

            <div className="mt-5 flex items-center justify-between rounded-md border border-destructive/40 bg-destructive/10 p-4">
              <div>
                <div className="font-mono-p text-[10px] uppercase tracking-widest text-destructive">Variance</div>
                <div className="font-display text-3xl font-black text-destructive">-55</div>
              </div>
              <button
                data-testid="pallet-find-variance"
                className="group flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2.5 font-display text-sm font-bold text-slate-900 transition-colors hover:border-volt hover:text-volt"
              >
                <Search className="h-4 w-4" />
                Find it
              </button>
            </div>
            <p className="mt-3 font-mono-p text-[11px] text-slate-400">Don't just see the variance. Drill into movement history.</p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export const OperatorConsole = () => (
  <section className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
    <div className="grid items-center gap-14 lg:grid-cols-2">
      <Reveal className="order-2 lg:order-1">
        <div className="relative overflow-hidden rounded-lg border border-slate-200">
          <img src={IMAGES.operator} alt="warehouse operator" className="h-[460px] w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B1240]/70 to-transparent" />
          <div className="absolute bottom-6 right-6 w-44 rounded-md border border-white/15 bg-[#0B1240]/85 p-3 backdrop-blur-md">
            <div className="mb-2 flex items-center gap-1.5">
              <Smartphone className="h-3.5 w-3.5 text-volt" />
              <span className="font-mono-p text-[9px] uppercase tracking-widest text-white/60">RF Console</span>
            </div>
            <div className="space-y-1.5 font-mono-p text-[10px] text-white">
              <div className="flex justify-between"><span className="text-white/50">PICK</span><span className="text-volt">A-12-04</span></div>
              <div className="flex justify-between"><span className="text-white/50">SKU</span><span>SKR-8841</span></div>
              <div className="flex justify-between"><span className="text-white/50">QTY</span><span>24</span></div>
              <div className="mt-2 bg-volt py-1.5 text-center font-bold text-white">SCAN TO CONFIRM</div>
            </div>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.1} className="order-1 lg:order-2">
        <Kicker num="06">Operator Console</Kicker>
        <SectionHeading className="mt-6">
          Your WMS shouldn't stop at the office door.
        </SectionHeading>
        <p className="mt-6 text-lg leading-relaxed text-slate-500">
          Put Stockaris in every operator's hand. RF scanning, guided picking, directed
          putaway and real-time validation — so the floor and the office finally speak
          the same language.
        </p>
        <ul className="mt-8 space-y-3">
          {["Guided pick & putaway", "Barcode / RF scan validation", "Live task queue per operator", "Zero paper, zero double-entry"].map((t) => (
            <li key={t} className="flex items-center gap-3 text-slate-600">
              <CheckCircle2 className="h-5 w-5 text-volt" strokeWidth={1.6} />
              {t}
            </li>
          ))}
        </ul>
      </Reveal>
    </div>
  </section>
);

export const ThreePL = () => (
  <section className="relative overflow-hidden border-y border-slate-200">
    <div className="absolute inset-0 -z-10">
      <img src={IMAGES.containers} alt="3PL containers" className="h-full w-full object-cover" />
      <div className="absolute inset-0 bg-[#0B1240]/88" />
    </div>
    <div className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
      <Reveal>
        <div className="flex items-center gap-3 font-mono-p text-xs uppercase tracking-[0.25em] text-volt">
          <span className="text-white/50">07</span>
          <span className="h-px w-8 bg-volt/70" />
          3PL
        </div>
        <h2 className="mt-6 max-w-3xl font-display text-4xl font-extrabold leading-[0.95] text-white sm:text-5xl lg:text-6xl">
          One platform. <span className="text-volt">Multiple customers.</span>
        </h2>
        <p className="mt-6 max-w-xl text-lg text-white/70">
          Run every client from a single system with complete data separation, per-customer
          billing, and dedicated portals — without spinning up a new instance each time.
        </p>
      </Reveal>

      <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { c: "Northgate Foods", u: "78%", p: "3,120" },
          { c: "Verra Retail", u: "54%", p: "1,880" },
          { c: "Kestrel Parts", u: "91%", p: "4,405" },
          { c: "Loom & Co", u: "36%", p: "962" },
        ].map((x, i) => (
          <Reveal key={x.c} delay={i * 0.06}>
            <div className="rounded-lg border border-white/15 bg-white/[0.06] p-5 backdrop-blur-sm transition-colors hover:border-volt/60">
              <div className="mb-4 flex items-center gap-2">
                <Building2 className="h-4 w-4 text-volt" strokeWidth={1.6} />
                <span className="font-display font-bold text-white">{x.c}</span>
              </div>
              <div className="font-mono-p text-[10px] uppercase tracking-wider text-white/50">Capacity used</div>
              <div className="mt-1 h-1.5 w-full bg-white/15">
                <div className="h-full bg-volt" style={{ width: x.u }} />
              </div>
              <div className="mt-3 flex justify-between font-mono-p text-xs">
                <span className="text-white/50">Pallets</span>
                <span className="text-white">{x.p}</span>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
